import { HttpResponse } from "@angular/common/http";
import { OperatorFunction } from "rxjs";
import { map } from "rxjs/operators";

/**
 * Conversiones de la pantalla de parámetros, aparte para poder probarlas
 * sin montar el componente.
 */

/**
 * Como sinSelloDeError, pero el motivo sale SOLO de la cabecera MESSAGE.
 *
 * MESSAGE la ponen SelloDeError y RestErrorHandler para un rechazo de
 * negocio (BusinessException), y es texto para la persona. Cuando algo
 * inesperado se escapa (la base no responde antes de entrar al método
 * transaccional, un cuerpo que Jackson no puede leer), RestErrorHandler
 * sella sin MESSAGE y pone en IDMESSAGE el texto de la excepción, en
 * inglés ("Could not open JDBC Connection for transaction"). El operador
 * compartido lo usaría como motivo y el aviso rojo lo pintaría tal cual.
 * Aquí, sin MESSAGE, va el texto por omisión.
 */
export function sinSelloConMotivoLegible<T>(porOmision: string): OperatorFunction<HttpResponse<T>, T> {
  return map((respuesta: HttpResponse<T>) => {
    if ((respuesta.headers.get("COD_STATUS") || "").toUpperCase() === "ERROR") {
      const motivo = (respuesta.headers.get("MESSAGE") || "").trim();
      throw new Error(motivo || porOmision);
    }
    return respuesta.body as T;
  });
}

/**
 * La hora de corte como la quiere el campo type="time": "HH:mm".
 *
 * El servidor la guarda como java.sql.Time y la devuelve "HH:mm:ss". Si
 * el campo recibe los segundos los pinta como un tercer segmento que
 * nadie pidió, y a partir de ahí la hora ya no se lee igual que en el
 * kiosco.
 */
export function horaParaCampo(hora: string | null | undefined): string | null {
  const texto = (hora ?? "").trim();
  if (!texto) {
    return null;
  }
  const partes = texto.split(":");
  if (partes.length < 2) {
    return texto;
  }
  return `${partes[0].padStart(2, "0")}:${partes[1].padStart(2, "0")}`;
}

/**
 * La hora de corte como la espera el servidor: "HH:mm:ss".
 *
 * El campo type="time" entrega "HH:mm" en cuanto alguien cambia la hora,
 * y Time.valueOf (con lo que Jackson arma un java.sql.Time) solo acepta
 * "HH:mm:ss": ese guardado tronaba al leer el cuerpo. El servidor ya
 * acepta las dos formas, pero mandar siempre la completa no depende de
 * eso. Vacía viaja como null, para que el servidor diga que falta.
 */
export function horaParaServidor(hora: string | null | undefined): string | null {
  const texto = (hora ?? "").trim();
  if (!texto) {
    return null;
  }
  const partes = texto.split(":");
  if (partes.length >= 3) {
    return texto;
  }
  const hh = partes[0].padStart(2, "0");
  const mm = (partes[1] ?? "00").padStart(2, "0");
  return `${hh}:${mm}:00`;
}

/**
 * El motivo que dio el servidor, o el texto por omisión.
 *
 * sinSelloDeError convierte el rechazo sellado en un Error con el motivo
 * de la cabecera MESSAGE. Un HttpErrorResponse (sin red, 500) no es un
 * Error y su `message` es técnico ("Http failure response for…"): para
 * ese caso va el texto por omisión.
 */
export function motivoDelRechazo(error: unknown, porOmision: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return porOmision;
}
